/**
 * Textos por variável do card do mapa da Visão Geral Agro (#1924).
 *
 * Conteúdo definido pela Bruna (TXT do WhatsApp em 26/08/2026 + reunião do
 * mesmo dia). Fica fora do JSX porque já são quatro variáveis e a lista cresce
 * pelo backend, não pelo frontend.
 */

/** Corpo do modal ⓘ de uma variável. */
export interface VariableInfo {
  /**
   * Linha `Período: X. Unidade: Y.` antes dos parágrafos. Falsa só para o ARM:
   * a metodologia usa o dado mais atual, não um período fechado (decisão da
   * Bruna na reunião de 26/08/2026, revertendo o pedido inicial).
   */
  showPeriod: boolean;
  paragraphs: string[];
}

/**
 * Mantido para variável sem texto cadastrado — é o parágrafo que o modal já
 * exibia antes do #1924. Sem ele, uma variável nova vinda do backend
 * renderizaria o modal com o corpo vazio.
 */
const FALLBACK_PARAGRAPH = 'Escala de cores provisória, distribuída uniformemente entre o '
  + 'mínimo e o máximo dos dados exibidos.';

const PARAGRAPHS: Record<string, string[]> = {
  arm: [
    'O ARM representa a quantidade de água retida nas camadas superficiais do solo, '
    + 'expressa como percentual da capacidade máxima de armazenamento.',
    'O cálculo considera precipitação acumulada, evapotranspiração potencial e '
    + 'propriedades físicas do solo de cada localidade monitorada.',
  ],
  saldo_bh: [
    'O saldo do balanço hídrico indica as regiões onde houve maior excedente ou '
    + 'déficit de água no solo nos últimos dias.',
    'O cálculo considera a relação entre a precipitação acumulada e a '
    + 'evapotranspiração, além das características físicas do solo de cada '
    + 'localidade, que determinam sua capacidade de armazenar e disponibilizar água.',
  ],
  risco_calor: [
    'O risco de calor indica o número de dias com temperaturas máximas previstas '
    + 'iguais ou superiores a 35 °C. Esse indicador permite identificar as áreas '
    + 'com maior frequência de exposição a temperaturas potencialmente estressantes '
    + 'para as culturas.',
  ],
  risco_frio: [
    'O risco de frio indica o número de dias com temperaturas mínimas previstas '
    + 'iguais ou menores a 10 °C. Esse indicador permite identificar as áreas '
    + 'com maior frequência de exposição a temperaturas potencialmente estressantes '
    + 'para as culturas.',
  ],
};

export function variableInfo(nome: string): VariableInfo {
  return {
    showPeriod: nome !== 'arm',
    paragraphs: PARAGRAPHS[nome] ?? [FALLBACK_PARAGRAPH],
  };
}

/**
 * Texto entre parênteses no cabeçalho da barra de escala, quando a unidade crua
 * não explica o número (pedido da Bruna, transcrição 7:45–8:35).
 *
 * ⚠️ NÃO reaproveitar como `unidade`: ela também monta as faixas do modal
 * (`0–1 dias`), que ficariam ilegíveis com o texto longo.
 */
const SCALE_UNIT_OVERRIDE: Record<string, string> = {
  risco_calor: 'número de dias com temperatura maior ou igual a 35 °C',
  risco_frio: 'número de dias com temperatura menor ou igual a 10 °C',
};

export function scaleHeading(nome: string, unidade: string): string {
  const texto = SCALE_UNIT_OVERRIDE[nome] ?? unidade;
  return `Escala da variável${texto ? ` (${texto})` : ''}`;
}
